/**
 * MentionTextarea — a controlled textarea with @-mention autocomplete.
 *
 * Typing "@" (at the start or after whitespace) opens a dropdown of the
 * `candidates` roster, filtered live as you keep typing. Arrow keys move
 * the highlight, Enter/Tab accept, Escape closes; clicking a row inserts
 * "@Full Name " at the caret. Selection is honest — it only ever inserts
 * a name that exists in `candidates`, so downstream `parseMentions` picks
 * it up unchanged.
 *
 * Drop-in for a plain <textarea value onChange>: it forwards className,
 * style, rows and placeholder so it matches whatever composer hosts it.
 */

import { useMemo, useRef, useState } from 'react';
import { C } from '../lib/tokens';

interface MentionTextareaProps {
  value: string;
  onChange: (next: string) => void;
  candidates: readonly string[];
  placeholder?: string;
  rows?: number;
  className?: string;
  style?: React.CSSProperties;
}

/** Case-insensitive match; prefixes rank above mid-string hits. Capped at 6. */
function filterCandidates(candidates: readonly string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  const list = q ? candidates.filter((c) => c.toLowerCase().includes(q)) : [...candidates];
  return list
    .slice()
    .sort((a, b) => {
      const ap = a.toLowerCase().startsWith(q) ? 0 : 1;
      const bp = b.toLowerCase().startsWith(q) ? 0 : 1;
      return ap - bp || a.localeCompare(b);
    })
    .slice(0, 6);
}

export function MentionTextarea({
  value,
  onChange,
  candidates,
  placeholder,
  rows = 2,
  className,
  style,
}: MentionTextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tokenStart, setTokenStart] = useState(-1);
  const [active, setActive] = useState(0);

  const matches = useMemo(
    () => (open ? filterCandidates(candidates, query) : []),
    [open, candidates, query],
  );

  /** Look at the text left of the caret for an in-progress "@token".
   *  Reads the element's live value (the `value` prop lags by one render
   *  inside the same onChange, which would miss the just-typed "@"). */
  const detect = (el: HTMLTextAreaElement) => {
    const caret = el.selectionStart ?? 0;
    const upto = el.value.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at === -1) {
      setOpen(false);
      return;
    }
    const before = at === 0 ? ' ' : upto[at - 1];
    const token = upto.slice(at + 1);
    // Only an @ that starts a word, and a token with no newline / not too long.
    if (!/\s/.test(before) || /\n/.test(token) || token.length > 40) {
      setOpen(false);
      return;
    }
    setQuery(token);
    setTokenStart(at);
    setActive(0);
    setOpen(true);
  };

  const choose = (name: string) => {
    const el = ref.current;
    const text = el?.value ?? value;
    const caret = el?.selectionStart ?? text.length;
    const head = text.slice(0, tokenStart);
    const tail = text.slice(caret);
    const inserted = `${head}@${name} ${tail}`;
    onChange(inserted);
    setOpen(false);
    const pos = head.length + name.length + 2; // "@" + name + " "
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      choose(matches[Math.min(active, matches.length - 1)]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={ref}
        className={className}
        style={style}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target);
        }}
        onKeyDown={onKeyDown}
        onClick={(e) => detect(e.currentTarget)}
        onKeyUp={(e) => {
          // Re-detect on caret moves that aren't the menu-nav keys.
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
            detect(e.currentTarget);
          }
        }}
        onBlur={() => {
          // Delay so an onMouseDown selection lands before we close.
          window.setTimeout(() => setOpen(false), 120);
        }}
      />
      {open && matches.length > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            zIndex: 40,
            minWidth: 200,
            maxWidth: 320,
            background: C.ink800,
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 8,
            boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
            overflow: 'hidden',
          }}
        >
          {matches.map((name, i) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={i === active}
              // onMouseDown (not onClick) so it fires before the textarea blur.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(name);
              }}
              onMouseEnter={() => setActive(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                textAlign: 'left',
                padding: '7px 10px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'inherit',
                color: i === active ? '#fff' : C.textMuted,
                background: i === active ? 'rgba(255,0,255,0.16)' : 'transparent',
              }}
            >
              <span style={{ color: C.magentaSoft }}>@</span>
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
